import Vue from "vue";
import { Module, VuexModule, Mutation, Action } from "vuex-module-decorators";

import { DIContainer } from "@/app.container";
import { Dictionary } from "@/lib/Dictionary.type";
import dataStore from "@/store";

import Attachment from "../attachment.model";
import Message from "../message.model";
import MessageCreate from "../message-create.interface";
import MessageUpdate from "../message-update.interface";

const name = "MessagingStore";

if ((dataStore.state as any)[name]) {
  dataStore.unregisterModule(name);
}

@Module({ name, dynamic: true, store: dataStore })
export default class MessagingStore extends VuexModule {
  attachment: Dictionary<Attachment> = {};
  message: Dictionary<Message> = {};

  messageAttachments: Dictionary<string[]> = {};
  plushieMessages: Dictionary<string[]> = {};

  // ################################### ATTACHMENTS #########################################

  get getAttachmentById(): (id: string) => Attachment | undefined {
    return (id: string) => this.attachment[id];
  }

  get getAttachmentsByMessageId(): (messageId: string) => Attachment[] {
    return (messageId: string) => {
      const attachmentIds = this.messageAttachments[messageId];

      if (!attachmentIds) {
        return [];
      }

      const result: Attachment[] = [];

      attachmentIds.forEach((id) => {
        const attachment = this.attachment[id];

        if (!attachment) {
          return;
        }

        result.push(this.attachment[id]);
      });

      result.sort((a, b) => (a.createdAt < b.createdAt ? 1 : -1));

      return result;
    };
  }

  @Mutation
  updateAttachment(payload: Attachment): void {
    Vue.set(this.attachment, payload.id, payload);

    if (this.messageAttachments[payload.message] === undefined) {
      return;
    }

    if (!this.messageAttachments[payload.message].includes(payload.id)) {
      this.messageAttachments[payload.message].push(payload.id);
    }
  }

  @Mutation
  updateMessageAttachments({
    messageId,
    attachments,
  }: {
    messageId: string;
    attachments: Attachment[];
  }): void {
    attachments.forEach((attachment) => {
      if (attachment.message !== messageId) {
        throw new Error(
          "All attachments should belong to the specified message!"
        );
      }
    });

    const attachmentIds: string[] = [];

    attachments.forEach((attachment) => {
      attachmentIds.push(attachment.id);

      Vue.set(this.attachment, attachment.id, attachment);
    });

    Vue.set(this.messageAttachments, messageId, attachmentIds);
  }

  @Action({ rawError: true })
  async createAttachment({
    messageId,
    storageItemId,
    mime,
  }: {
    messageId: string;
    storageItemId: string;
    mime: string;
  }): Promise<Attachment> {
    const item = await DIContainer.AttachmentRepository.createAttachment(
      messageId,
      storageItemId,
      mime
    );

    this.updateAttachment(item);

    return item;
  }

  @Action({ rawError: true })
  async loadAttachmentsByMessageIds({
    messageIds,
    useCache = true,
  }: {
    messageIds: string[];
    useCache?: boolean;
  }): Promise<Dictionary<Attachment[]>> {
    const missing: string[] = [];
    const result: Dictionary<Attachment[]> = {};

    messageIds.forEach((messageId) => {
      if (useCache && this.messageAttachments[messageId]) {
        result[messageId] = this.getAttachmentsByMessageId(messageId);
        return;
      }

      missing.push(messageId);
    });

    if (!missing.length) {
      return result;
    }

    const items = await DIContainer.AttachmentRepository.getByMessageIds(
      missing
    );

    const messageAttachments: Dictionary<Attachment[]> = {};

    items.forEach((item) => {
      if (!messageAttachments[item.message]) {
        messageAttachments[item.message] = [];
      }

      messageAttachments[item.message].push(item);
    });

    for (const messageId in messageAttachments) {
      const attachments = messageAttachments[messageId];

      if (!attachments) {
        continue;
      }

      this.updateMessageAttachments({ messageId, attachments });
    }

    return { ...result, ...messageAttachments };
  }

  // ################################### MESSAGES #########################################

  get getMessageById(): (id: string) => Message | undefined {
    return (id: string) => this.message[id];
  }

  get getMessagesByPlushieId(): (plushieId: string) => Message[] {
    return (plushieId: string) => {
      const messageIds = this.plushieMessages[plushieId];

      if (!messageIds) {
        return [];
      }

      const result: Message[] = [];

      messageIds.forEach((id) => {
        const message = this.message[id];

        if (!message) {
          return;
        }

        result.push(this.message[id]);
      });

      result.sort((a, b) => (a.createdAt > b.createdAt ? 1 : -1));

      return result;
    };
  }

  @Mutation
  removeMessage(payload: Message) {
    const plushieRelations = this.plushieMessages[payload.plushie];
    let index = -1;

    if (plushieRelations) {
      index = plushieRelations.indexOf(payload.id);
    }

    if (index !== -1) {
      plushieRelations.splice(index, 1);
    }

    if (this.messageAttachments[payload.id]) {
      this.messageAttachments[payload.id].forEach((attachmentId) => {
        delete this.attachment[attachmentId];
      });

      delete this.messageAttachments[payload.id];
    }

    delete this.message[payload.id];
  }

  @Mutation
  updateMessage(payload: Message): void {
    Vue.set(this.message, payload.id, payload);

    if (
      Array.isArray(this.plushieMessages[payload.plushie]) &&
      !this.plushieMessages[payload.plushie].includes(payload.id)
    ) {
      this.plushieMessages[payload.plushie].push(payload.id);
    }
  }

  @Mutation
  updateMessages({
    plushieId,
    messages,
  }: {
    plushieId: string;
    messages: Message[];
  }): void {
    messages.forEach((message) => {
      if (message.plushie !== plushieId) {
        throw new Error("All messages should belong to the specified plushie!");
      }
    });

    const plushieMessages: string[] = [];

    messages.forEach((message) => {
      plushieMessages.push(message.id);
      Vue.set(this.message, message.id, message);
    });

    Vue.set(this.plushieMessages, plushieId, plushieMessages);
  }

  @Action({ rawError: true })
  async loadMessagesByPlushieId({
    plushieId,
    useCache = true,
  }: {
    plushieId: string;
    useCache?: boolean;
  }): Promise<Message[]> {
    let messages: Message[] = [];

    if (useCache && this.plushieMessages[plushieId]) {
      const messageIds = this.plushieMessages[plushieId];

      messageIds.forEach((id) => {
        messages.push(this.message[id]);
      });

      return messages;
    }

    messages = await DIContainer.MessageRepository.getByPlushie(plushieId);

    this.updateMessages({ plushieId, messages });

    return messages;
  }

  @Action({ rawError: true })
  async createMessage(message: MessageCreate): Promise<Message> {
    const item = await DIContainer.MessageRepository.createMessage(message);

    this.updateMessage(item);
    this.updateMessageAttachments({ messageId: item.id, attachments: [] });

    if (!message.attachments) {
      return item;
    }

    const attachmentCreatePromises: Promise<Attachment>[] = [];
    message.attachments.forEach((storageItem) => {
      attachmentCreatePromises.push(
        this.createAttachment({
          messageId: message.id,
          storageItemId: storageItem.id,
          mime: storageItem.mime,
        })
      );
    });

    await Promise.all(attachmentCreatePromises);

    return item;
  }

  @Action({ rawError: true })
  async deleteMessage(message: Message): Promise<void> {
    await DIContainer.MessageRepository.delete(message);

    this.removeMessage(message);

    return;
  }

  @Action({ rawError: true })
  async markMessageAsRead(messageId: string): Promise<Message | undefined> {
    const item = await DIContainer.MessageRepository.markAsRead(messageId);

    if (!item) {
      return;
    }

    this.updateMessage(item);

    return item;
  }

  @Action({ rawError: true })
  async updateMessageText(message: MessageUpdate): Promise<Message> {
    const item = await DIContainer.MessageRepository.updateMessage(message);

    this.updateMessage(item);

    return item;
  }
}
